import { useState } from 'react'
import { nodeOffset, trackFraction, type Milestone, type MilestoneNodeColorOptions } from './milestoneHelpers'
import { FaHeart, FaMedal, FaRegEnvelope, FaQuestion } from 'react-icons/fa6'
import { LuPartyPopper } from 'react-icons/lu'
import { GiOpenBook } from 'react-icons/gi'
import { FiGift } from 'react-icons/fi'
import { CastleIcon } from './icons'
import { useTranslation } from 'react-i18next'
import { DISNEY_TOS_URL } from '@/util/defaults'
import { sendDisneyTosClickedEvent } from '@/services/analytics/gtm'

const DONE_VARIANT_STYLES: Record<MilestoneNodeColorOptions, string> = {
  blue: 'border-cta bg-cta text-white',
  green: 'border-leaf-green bg-leaf-green text-white',
  gold: 'border-transparent bg-gold text-white shadow-gold-glow [&_svg]:drop-shadow-gold-icon',
  silver: 'border-transparent bg-silver text-white shadow-silver-glow [&_svg]:drop-shadow-silver-icon',
  bronze: 'border-transparent bg-bronze text-white shadow-bronze-glow [&_svg]:drop-shadow-bronze-icon',
}

export interface MilestoneNodeProps {
  milestone: Milestone
  value: number
  max: number
}

export default function MilestoneNode({ milestone, value, max }: MilestoneNodeProps) {
  const { t } = useTranslation()
  const completeAt = milestone.completeAt ?? milestone.position
  const done = value >= completeAt

  // Detect an upward crossing of the goal during render (the documented
  // "storing information from previous renders" pattern). Bumping the token
  // remounts the node via `key`, replaying the one-shot pulse animation.
  const [prevValue, setPrevValue] = useState(value)
  const [pulseToken, setPulseToken] = useState(0)
  if (value !== prevValue) {
    if (prevValue < completeAt && value >= completeAt) setPulseToken((t) => t + 1)
    setPrevValue(value)
  }

  let id: string = `milestone-${milestone.position}`
  let icon: React.ReactNode = <LuPartyPopper />
  let completeColor: MilestoneNodeColorOptions = 'green'
  let caption: React.ReactNode
  let subCaption: React.ReactNode
  let captionAbbreviated: React.ReactNode

  switch (milestone.variant) {
    case 'initial-gift':
      id = 'initial-gift-node'
      icon = <FaHeart />
      completeColor = 'green'
      break
    case 'initial-invitation':
      id = 'initial-invitation-node'
      icon = <FaRegEnvelope />
      completeColor = 'green'
      caption = t('content.components.prize_progress.started_caption')
      break
    case 'participation':
      id = 'participation-node'
      icon = <FiGift />
      completeColor = 'green'
      caption = t('content.components.prize_progress.participation_caption')
      captionAbbreviated = t('content.components.prize_progress.participation_caption').split(' ')[0]
      subCaption = t('content.components.prize_progress.invite_count', { count: milestone.position })
      break
    case 'disney':
      id = 'disney-node'
      icon = <CastleIcon />
      completeColor = 'gold'
      caption = (
        <a
          href={DISNEY_TOS_URL}
          target='_blank'
          rel='noopener noreferrer'
          className='hover:text-cta underline decoration-dotted underline-offset-2'
          onClick={() => sendDisneyTosClickedEvent(value)}
        >
          {t('content.components.prize_progress.disney_caption')}
        </a>
      )

      captionAbbreviated = (
        <a
          href={DISNEY_TOS_URL}
          target='_blank'
          rel='noopener noreferrer'
          className='hover:text-cta underline decoration-dotted underline-offset-2'
          onClick={() => sendDisneyTosClickedEvent(value)}
        >
          {t('content.components.prize_progress.disney_caption').split(' ')[0]}
        </a>
      )
      subCaption = t('content.components.prize_progress.invite_count', { count: milestone.position })
      break
    case 'books':
      id = 'books-node'
      icon = <GiOpenBook />
      completeColor = 'green'
      caption = `$${milestone.position}`
      break
    case 'medal':
      id = `${milestone.color ?? 'gold'}-medal-node`
      icon = <FaMedal />
      completeColor = milestone.color ?? 'gold'
      caption = `$${milestone.position}`
      break
    case 'goal':
      id = 'goal-node'
      icon = <LuPartyPopper />
      completeColor = 'green'
      caption = `$${milestone.position}`
      break
    default:
      icon = <FaQuestion />
  }

  const doneStyle = DONE_VARIANT_STYLES[completeColor]
  const pendingStyle = 'border-pending text-pending-txt bg-white'

  return (
    <div
      id={id}
      className='absolute top-9 -translate-x-1/2 -translate-y-1/2 text-center'
      style={{ left: nodeOffset(trackFraction(milestone.position, max)) }}
    >
      <div
        key={pulseToken}
        className={`grid size-11 place-items-center rounded-full border-3 transition-all duration-300 md:size-13.5 [&_svg]:size-5 md:[&_svg]:size-6 ${done ? doneStyle : pendingStyle} ${pulseToken > 0 ? 'animate-node-pulse' : ''}`}
      >
        {icon}
      </div>
      {caption && (
        <div className='text-secondary-txt absolute top-12 left-1/2 -translate-x-1/2 text-sm leading-tight font-bold md:top-15 md:whitespace-nowrap'>
          <div className='hidden lg:inline whitespace-nowrap'>{caption ?? ''}</div>

          <div className='visible lg:hidden whitespace-nowrap'>{captionAbbreviated ?? caption}</div>

          {subCaption && (
            <span className='text-tertiary-txt block text-sm font-light md:text-sm whitespace-nowrap'>
              {subCaption}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
